'use client';

import type { ForwardedRef, InputHTMLAttributes, ReactNode } from 'react';

import type { VariantProps } from '@zoomit/primitives';
import { cn, cva } from '@zoomit/primitives';

const inputWrapperVariants = cva({
  base: 'transition-border flex w-full flex-nowrap items-center duration-200 rounded-sm bg-background2',
  variants: {
    variant: {
      default: '',
      rounded: 'rounded-full',
      fill: 'rounded-full bg-grey6 border-none',
    },
    size: {
      default: ['px-2', 'border-grey3 border focus-within:border-primary3 dark:border-none'],
      large: 'px-1.5 border-2 border-primary3 border-solid',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

const inputVariants = cva({
  base: [
    'rounded-inherit text-body peer flex-grow border-none outline-none',
    'w-full bg-transparent',
    'text-[#383e43] focus:outline-none dark:text-white',
  ],
  variants: {
    size: {
      default: 'h-8',
      large: 'h-11',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

type InputProps = {
  variant?: VariantProps<typeof inputWrapperVariants>['variant'];
  inputSize?: VariantProps<typeof inputVariants>['size'];
  rightNode?: ReactNode;
  leftNode?: ReactNode;
  ref?: ForwardedRef<HTMLInputElement>;
  error?: boolean;
  placeholder: React.ReactNode | string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'placeholder'>;

export const InputWithRichPlaceholder = (props: InputProps) => {
  const { variant, inputSize, leftNode, rightNode, error, className, placeholder, ...rest } = props;

  return (
    <div className={cn(inputWrapperVariants({ variant: variant, size: inputSize }), className)}>
      <i className="flex items-center">{rightNode}</i>
      <div className="relative w-full px-2">
        <input className={inputVariants({ size: inputSize })} placeholder=" " {...rest} />
        {placeholder && (
          <div
            className={cn(
              'text-grey1 text-body pointer-events-none absolute top-1/2 -translate-y-1/2',
              '-translate-x-4 opacity-0 transition-[opacity,translate] peer-placeholder-shown:translate-x-0 peer-placeholder-shown:opacity-100',
            )}
          >
            {placeholder}
          </div>
        )}
      </div>
      <i className="flex items-center">{leftNode}</i>
    </div>
  );
};
